import type { HTMLAttributes } from 'react';

import './tabs.css';

export type TabsSize = '1920+1440' | '768' | '375';
export type TabsPosition = 1 | 2 | 3 | 4 | 5;

export interface TabSlot {
  label: string;
  badge: string | number;
}

export type TabsSlots = readonly [TabSlot, TabSlot, TabSlot, TabSlot, TabSlot];

type TabsNativeProps = Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'onChange' | 'style'>;

export interface TabsProps extends TabsNativeProps {
  size: TabsSize;
  active: TabsPosition;
  slots: TabsSlots;
  onChange?: (position: TabsPosition) => void;
}

const FIGMA_NODE: Record<TabsSize, Record<TabsPosition, string>> = {
  '1920+1440': {
    1: '137:35828',
    2: '137:35854',
    3: '137:35885',
    4: '137:35916',
    5: '137:35947',
  },
  768: {
    1: '137:38126',
    2: '137:38147',
    3: '137:38168',
    4: '137:38189',
    5: '137:38210',
  },
  375: {
    1: '137:40954',
    2: '137:41064',
    3: '137:41116',
    4: '137:41168',
    5: '137:41220',
  },
};

const FIGMA_VARIANT: Record<TabsSize, Record<TabsPosition, string>> = {
  '1920+1440': {
    1: 'Property 1=1, size=1920+1440',
    2: 'Property 1=2, size=1920+1440',
    3: 'Property 1=3, size=1920+1440',
    4: 'Property 1=4, size=1920+1440',
    5: 'Property 1=5, size=1920+1440',
  },
  768: {
    1: 'Property 1=1, size=768',
    2: 'Property 1=2, size=768',
    3: 'Property 1=3, size=768',
    4: 'Property 1=4, size=768',
    5: 'Property 1=5, size=768',
  },
  375: {
    1: 'Property 1=1, size=375',
    2: 'Property 1=2, size=375',
    3: 'Property 1=2, size=375',
    4: 'Property 1=2, size=375',
    5: 'Property 1=2, size=375',
  },
};

const SIZE_CLASS: Record<TabsSize, string> = {
  '1920+1440': 'desktop',
  768: 'tablet',
  375: 'mobile',
};

export function Tabs({
  size,
  active,
  slots,
  onChange,
  className = '',
  ...props
}: TabsProps) {
  const classes = ['lovo-tabs', `lovo-tabs--${SIZE_CLASS[size]}`, className]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      {...props}
      className={classes}
      data-figma-node={FIGMA_NODE[size][active]}
      data-figma-variant={FIGMA_VARIANT[size][active]}
      role="tablist"
    >
      {slots.map((slot, index) => {
        const position = (index + 1) as TabsPosition;
        const isActive = position === active;

        return (
          <button
            aria-selected={isActive}
            className={[
              'lovo-tabs__tab',
              `lovo-tabs__tab--position-${position}`,
              isActive ? 'lovo-tabs__tab--active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            key={position}
            onClick={() => onChange?.(position)}
            role="tab"
            tabIndex={isActive ? 0 : -1}
            type="button"
          >
            <span className="lovo-tabs__label">{slot.label}</span>
            <span className="lovo-tabs__badge">{slot.badge}</span>
          </button>
        );
      })}
    </div>
  );
}
